/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// Batch D P0-18a — persistent-notification fallback for the lock-screen
// ETA HUD. The proper iOS Live Activity (ActivityKit Widget Extension)
// is a separate batch (filed as Build 92 P0); this fallback ships the
// same safety-perception win (live ETA on the lock screen) without the
// new Xcode target and Swift activity attributes.
//
// Implementation: Capacitor LocalNotifications. We schedule a single
// notification with a stable id when the walk starts, then update it
// each minute by cancelling + rescheduling under the same id. iOS
// merges the dismiss + reissue when both happen in the same tick so the
// notification looks like it's updating in place.
//
// Web no-op: returns "unsupported" so the WMH context can fall through.

import { Capacitor } from "@capacitor/core";
import {
  LocalNotifications,
  type LocalNotificationSchema,
} from "@capacitor/local-notifications";

const NOTIFICATION_ID = 700_001; // stable so subsequent posts replace
const CHANNEL_ID = "wmh-progress";

export type EtaUpdateOutcome = "scheduled" | "denied" | "unsupported" | "error";

export interface EtaNotificationContent {
  destinationName: string;
  etaMinutes: number;
  /** When the walker started — used to render "started 3 min ago" sub-text. */
  startedAt: number;
}

function formatBody(content: EtaNotificationContent, now: number): string {
  const elapsedMs = Math.max(0, now - content.startedAt);
  const elapsedMin = Math.floor(elapsedMs / 60_000);
  if (content.etaMinutes <= 0) {
    return `Approaching ${content.destinationName} • Started ${elapsedMin} min ago`;
  }
  return `${content.etaMinutes} min to ${content.destinationName} • Started ${elapsedMin} min ago`;
}

async function ensurePermission(): Promise<boolean> {
  try {
    const status = await LocalNotifications.checkPermissions();
    if (status.display === "granted") return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === "granted";
  } catch {
    return false;
  }
}

async function ensureChannel(): Promise<void> {
  if (Capacitor.getPlatform() !== "android") return;
  try {
    await LocalNotifications.createChannel({
      id: CHANNEL_ID,
      name: "Walk Me Home progress",
      description: "Live tracking status while a Walk Me Home is active.",
      importance: 3,
      visibility: 1,
      sound: undefined,
    });
  } catch {
    /* tolerated — channel may already exist */
  }
}

export async function showEtaNotification(
  content: EtaNotificationContent,
): Promise<EtaUpdateOutcome> {
  if (Capacitor.getPlatform() === "web") return "unsupported";
  const ok = await ensurePermission();
  if (!ok) return "denied";
  await ensureChannel();
  // Cancel any previous instance under the same id so iOS treats this as a
  // refresh of the lock-screen card rather than a brand-new alert.
  try {
    await LocalNotifications.cancel({
      notifications: [{ id: NOTIFICATION_ID }],
    });
  } catch {
    /* tolerated — first call has nothing to cancel */
  }
  const notif: LocalNotificationSchema = {
    id: NOTIFICATION_ID,
    title: "Walk Me Home",
    body: formatBody(content, Date.now()),
    ongoing: true,
    autoCancel: false,
    schedule: { at: new Date(Date.now() + 200) },
    channelId: CHANNEL_ID,
  };
  try {
    await LocalNotifications.schedule({ notifications: [notif] });
    return "scheduled";
  } catch (err) {
    console.warn("[wmh-notification] schedule failed:", err);
    return "error";
  }
}

export async function hideEtaNotification(): Promise<void> {
  if (Capacitor.getPlatform() === "web") return;
  try {
    await LocalNotifications.cancel({
      notifications: [{ id: NOTIFICATION_ID }],
    });
  } catch {
    /* tolerated */
  }
}

// Exposed for testability — the WMH context drives the cadence so this
// helper stays a thin scheduler.
export const __testing = {
  formatBody,
  NOTIFICATION_ID,
};
